import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, Input, Paragraph, ScrollView, Spinner, XStack, YStack } from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import { useSaveTraining, ocrPreviewStore } from '@mrrmlab/feature-shopping-list';
import type { DebugParseImageResponse, SaveTrainingLine } from '@mrrmlab/api-client';
import { ocrAnalyzeStore } from '../ocr-analyze-store';

interface Entry {
  id: string;
  crop: string;
  originalText: string;
  text: string;
  confidence: number;
}

export function ListImageAnalyzeRoute() {
  const { listId } = useParams({ from: '/lists/$listId/image-analyze' });
  const navigate = useNavigate();
  const saveTraining = useSaveTraining();

  const [result] = useState<DebugParseImageResponse | null>(() => ocrAnalyzeStore.consume());
  const [entries, setEntries] = useState<Entry[]>(() =>
    (result?.lines ?? []).map((line, i) => ({
      id: String(i),
      crop: line.crop,
      originalText: line.text,
      text: line.text,
      confidence: line.confidence,
    })),
  );
  const [enlargedCrop, setEnlargedCrop] = useState<string | null>(null);

  const back = () => void navigate({ to: '/lists/$listId', params: { listId } });

  const setText = (id: string, text: string) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, text } : e)));

  const remove = (id: string) => setEntries((prev) => prev.filter((e) => e.id !== id));

  const handleConfirm = () => {
    const kept = entries.filter((e) => e.text.trim().length >= 2);

    if (kept.length > 0) {
      const lines: SaveTrainingLine[] = kept.map((e) => ({
        crop: e.crop,
        originalText: e.originalText,
        correctedText: e.text !== e.originalText ? e.text : null,
        confidence: e.confidence,
      }));
      saveTraining.mutate({ lines, model: 'easyocr/de' });
    }

    if (result?.preprocessedImage) {
      ocrPreviewStore.set(result.preprocessedImage);
    }

    const items = kept.map((e) => ({ name: e.text.trim() }));
    void navigate({
      to: '/lists/$listId/review',
      params: { listId },
      search: { items: JSON.stringify(items) },
    });
  };

  const keptCount = entries.filter((e) => e.text.trim().length >= 2).length;

  return (
    <Screen>
      {enlargedCrop && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
          onClick={() => setEnlargedCrop(null)}
        >
          <img
            src={`data:image/jpeg;base64,${enlargedCrop}`}
            alt="Vergrößert"
            style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 8 }}
          />
        </div>
      )}

      <XStack justifyContent="space-between" alignItems="center">
        <Button size="$2" chromeless onPress={back} disabled={saveTraining.isPending}>
          ← Zurück
        </Button>
        <Paragraph fontWeight="600">Zeilen korrigieren</Paragraph>
        <Button
          size="$3"
          theme="active"
          disabled={keptCount === 0}
          onPress={handleConfirm}
        >
          {saveTraining.isPending ? <Spinner size="small" /> : `${keptCount} übernehmen`}
        </Button>
      </XStack>

      {!result && (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Paragraph opacity={0.5}>Kein Analyseergebnis vorhanden.</Paragraph>
        </YStack>
      )}

      {result && entries.length === 0 && (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Paragraph opacity={0.5} fontStyle="italic">Keine Zeilen erkannt.</Paragraph>
        </YStack>
      )}

      {entries.length > 0 && (
        <ScrollView flex={1}>
          <YStack gap="$3" paddingVertical="$2">
            {entries.map((entry) => (
              <XStack key={entry.id} gap="$2" alignItems="center">
                <div
                  style={{ flexShrink: 0, cursor: 'zoom-in' }}
                  onClick={() => entry.crop && setEnlargedCrop(entry.crop)}
                >
                  {entry.crop ? (
                    <img
                      src={`data:image/jpeg;base64,${entry.crop}`}
                      alt="Ausschnitt"
                      style={{
                        height: 52,
                        width: 110,
                        objectFit: 'contain',
                        borderRadius: 4,
                        border: entry.confidence < 0.5
                          ? '2px solid orange'
                          : '1px solid #ccc',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <div style={{ width: 110, height: 52 }} />
                  )}
                  <Paragraph fontSize="$1" opacity={0.45} textAlign="center">
                    {Math.round(entry.confidence * 100)} %
                  </Paragraph>
                </div>

                <Input
                  flex={1}
                  size="$3"
                  value={entry.text}
                  onChangeText={(t) => setText(entry.id, t)}
                  borderColor={entry.confidence < 0.5 ? '$orange8' : undefined}
                />

                <Button size="$2" chromeless onPress={() => remove(entry.id)}>
                  ✕
                </Button>
              </XStack>
            ))}
          </YStack>
        </ScrollView>
      )}
    </Screen>
  );
}
